import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks';
import { authApi } from '@/api';
import { ApiError } from '@/api/client';
import {
  BusyButton,
  CardIntro,
  EmailChip,
  FieldError,
  FormAlert,
  PasswordInput,
  PasswordRules,
  StatusMark,
  emailMsg,
  linkCls,
  mmss,
  passwordMsg,
  useCountdown,
} from '@/components/auth-kit';

const registerSchema = z
  .object({
    name: z.string().refine((v) => v.trim().length >= 2, 'Enter your full name (at least 2 characters).'),
    email: z.string().superRefine((v, ctx) => {
      const m = emailMsg(v);
      if (m) ctx.addIssue({ code: 'custom', message: m });
    }),
    password: z.string().superRefine((v, ctx) => {
      const m = passwordMsg(v);
      if (m) ctx.addIssue({ code: 'custom', message: m });
    }),
    confirmPassword: z.string().min(1, 'Repeat your password.'),
    terms: z.boolean().refine((v) => v, 'Accept the terms to create an account.'),
  })
  .refine((d) => !d.confirmPassword || d.password === d.confirmPassword, {
    message: 'Passwords don’t match.',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<{ title: string; conflict?: boolean; body?: string } | null>(
    null
  );
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const countdown = useCountdown();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    setFocus,
    trigger,
    getValues,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { name: '', email: '', password: '', confirmPassword: '', terms: false },
  });

  const password = watch('password');
  const terms = watch('terms');

  useEffect(() => {
    document.title = `${sentTo ? 'Check your inbox' : 'Create an account'} · authrz.me`;
  }, [sentTo]);

  const onSubmit = async (data: RegisterFormData) => {
    setAlert(null);
    setIsLoading(true);
    try {
      await authApi.register({
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password,
      });
      // Session now exists; send the verification email (best effort).
      try {
        await authApi.resendVerificationEmail();
      } catch {
        /* surfaced via Resend on the next screen */
      }
      setSentTo(data.email.trim().toLowerCase());
      countdown.start(30);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setError('email', { message: 'An account with this email already exists.' });
        setAlert({ title: 'This email is already registered', conflict: true });
        setFocus('email');
      } else {
        setAlert({
          title: 'Couldn’t create your account',
          body: error instanceof Error ? error.message : 'Something went wrong. Try again.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authApi.resendVerificationEmail();
      toast({ title: 'Verification email sent', description: sentTo ?? undefined });
      countdown.start(30);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Couldn’t resend the email',
        description: error instanceof Error ? error.message : 'Try again in a moment.',
      });
    } finally {
      setResending(false);
    }
  };

  const goToConsole = async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth'] });
    navigate('/dashboard');
  };

  if (sentTo) {
    return (
      <section className="flex flex-col gap-6" aria-labelledby="doneTitle" aria-live="polite">
        <CardIntro
          id="doneTitle"
          mark={
            <StatusMark tone="ok">
              <Mail />
            </StatusMark>
          }
          title="Check your inbox"
          sub={
            <>
              We sent a verification link to <EmailChip>{sentTo}</EmailChip>. Open it to verify your
              address.
            </>
          }
        />
        <div className="flex items-center justify-between gap-3 text-[13px] text-muted-foreground">
          <span>
            {countdown.left > 0 ? (
              <>
                Resend available in <span className="text-foreground tabular-nums">{mmss(countdown.left)}</span>
              </>
            ) : (
              'Didn’t get it? Check spam, or resend.'
            )}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={countdown.left > 0 || resending}
            onClick={resend}
          >
            Resend email
          </Button>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="lg" className="w-full" onClick={goToConsole}>
            Continue to console
          </Button>
        </div>
      </section>
    );
  }

  const field = (name: 'name' | 'email') => ({
    ...register(name),
    'aria-invalid': !!errors[name],
    'aria-describedby': errors[name] ? `${name}-err` : undefined,
  });

  return (
    <section className="flex flex-col gap-6" aria-labelledby="regTitle">
      <CardIntro
        id="regTitle"
        title="Create an account"
        sub="You’ll verify your email after signing up."
      />
      {alert && (
        <FormAlert title={alert.title}>
          {alert.conflict ? (
            <>
              <Link className={linkCls} to="/login">
                Sign in
              </Link>{' '}
              instead, or{' '}
              <Link className={linkCls} to="/forgot-password">
                reset your password
              </Link>
              .
            </>
          ) : (
            alert.body
          )}
        </FormAlert>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            type="text"
            placeholder="Ana Duarte"
            autoComplete="name"
            autoFocus
            className="h-11"
            {...field('name')}
          />
          <FieldError id="name-err">{errors.name?.message}</FieldError>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            autoComplete="email"
            className="h-11"
            {...field('email')}
          />
          <FieldError id="email-err">{errors.email?.message}</FieldError>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            placeholder="Create a password"
            autoComplete="new-password"
            aria-describedby="pwRules"
            aria-invalid={!!errors.password}
            {...register('password', {
              onChange: () => {
                if (getValues('confirmPassword')) void trigger('confirmPassword');
              },
            })}
          />
          <PasswordRules id="pwRules" value={password} />
          <FieldError id="password-err">{errors.password?.message}</FieldError>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <PasswordInput
            id="confirmPassword"
            placeholder="Repeat the password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirmPassword}
            aria-describedby={errors.confirmPassword ? 'confirm-err' : undefined}
            {...register('confirmPassword')}
          />
          <FieldError id="confirm-err">{errors.confirmPassword?.message}</FieldError>
        </div>
        <div className="space-y-1.5">
          <label className="flex cursor-pointer items-center gap-2 text-[13.5px]">
            <Checkbox
              id="terms"
              checked={terms}
              aria-invalid={!!errors.terms}
              onCheckedChange={(c) => setValue('terms', !!c, { shouldValidate: true })}
            />
            I agree to the terms of service and privacy policy
          </label>
          <FieldError id="terms-err">{errors.terms?.message}</FieldError>
        </div>
        <BusyButton type="submit" busy={isLoading} busyLabel="Creating account…">
          Create account
        </BusyButton>
      </form>
      <p className="text-center text-[13.5px] text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className={linkCls}>
          Sign in
        </Link>
      </p>
    </section>
  );
}
